import { Inject, Injectable } from "@angular/core";
import { Platform, SentryLogger } from "@bca-core/models/_enums";
import { BCA_PLATFORM } from "@bca-core/models/platform";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { infoMessagesFilter } from "@bca-shared/shared.constants";
import { IRootState } from "@bca-store/store.reducers";
import { selectAllProducts } from "@bca-store/store.selectors";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/browser";
import { first } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class SentryService {
  private readonly _debugPlatforms = Array(
    Platform.ACCEPTANCE,
    Platform.TEST,
    Platform.TEST2,
    Platform.TESTAUT,
    Platform.DEVELOPMENT,
  );

  chromeIOSMobileError =
    /CriOS/i.test(navigator.userAgent) &&
    /iphone|ipod|ipad/i.test(navigator.userAgent);

  constructor(
    @Inject(BCA_PLATFORM) private readonly _platform: Platform,
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration,
    private readonly _store$: Store<IRootState>,
  ) {}

  /**
   * Log an error to sentry
   * @param error the error/exception
   * @param context (optional) provide extra information
   */
  public async logError(error: any, context?: Object): Promise<void> {
    const products = await this._store$
      .pipe(select(selectAllProducts), first())
      .toPromise();

    Sentry.withScope((scope) => {
      const ctx = { ...context, products };
      scope.setExtra("context", ctx);
      scope.setExtra("errorMessage", { message: error.message || error });
      scope.setTag("logger", SentryLogger.ERROR);
      scope.setLevel("error");

      Sentry.captureException(error.originalError || error);

      if (this._debugPlatforms.includes(this._platform)) {
        console.error(`[sentry-e] => `, { error, context: ctx });
      }
    });
  }

  /**
   * Log an info message to sentry
   * @param message the message
   * @param context (optional) provide extra information
   */
  public async logInfo(message: string, context?: Object): Promise<void> {
    const filteredInfoMessages = infoMessagesFilter();
    if (
      filteredInfoMessages.some((filteredInfoMessage) =>
        message.includes(filteredInfoMessage),
      ) ||
      this.chromeIOSMobileError
    )
      return;

    const products = await this._store$
      .pipe(select(selectAllProducts), first())
      .toPromise();
    Sentry.withScope((scope) => {
      const ctx = { ...context, products };
      scope.setExtra("context", ctx);
      scope.setTag("logger", SentryLogger.INFO);
      scope.setLevel("info");

      Sentry.captureMessage(message);

      if (this._debugPlatforms.includes(this._platform)) {
        console.info(`[sentry-i] => `, { message, context: ctx });
      }
    });
  }

  /**
   * Log a debug message to sentry
   * @param message the message
   * @param context (optional) provide extra information
   */
  public logDebug(message: string, context?: Object): void {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setExtra("context", context);
      }
      scope.setTag("logger", SentryLogger.DEBUG);
      scope.setLevel("debug");

      Sentry.captureMessage(message);

      if (this._debugPlatforms.includes(this._platform)) {
        console.info(`[sentry-d] => `, { message, context });
      }
    });
  }
}
